import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Users, Mail, Shield, Building2, Check, Loader2, Lock, Search, User as UserIcon
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ServiceToggle from './ServiceToggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import TabPageLayout from './TabPageLayout';
import EmptyState from './EmptyState';
import { SecondaryButton, SuccessButton, LayoutActionButton } from './Buttons';
import StandardModal from './StandardModal';
import ResourceCard from './ResourceCard';
import ModalTabSelector from './ModalTabSelector';
import DeleteConfirmModal from './DeleteConfirmModal';
import PinModal from './PinModal';
import { BACKEND_URL } from '../config/api';
import { getAdminUserData } from '../utils/superAdmin';

const PLATFORM_API = `${BACKEND_URL}/api/v2/super-admin`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
// Sensitive team/role changes need step-up verification headers
const stepUpHeaders = (_pin, token) => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
    'X-Stepup-Token': token,
    'X-Admin-Otp': token,
  },
});

const CHANNEL_OPTIONS = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
];

const emptyForm = {
  username: '', name: '', email: '', password: '',
  role_id: '', commission_rate: 0, is_active: true,
  phone: '', two_factor_channels: ['email'], regions: [],
};

const REGION_OPTIONS = ['UAE', 'USA'];

const SuperAdminTeamTab = () => {
  const me = getAdminUserData();
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [modalTab, setModalTab] = useState('basic');
  const [form, setForm] = useState(emptyForm);
  const [selectedOrgIds, setSelectedOrgIds] = useState(new Set());
  const [orgSearch, setOrgSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // PIN + OTP step-up prompt for sensitive team/role changes
  const [pinPrompt, setPinPrompt] = useState(null);
  const requirePin = (label, run) => setPinPrompt({ label, run });

  const fetchData = useCallback(async () => {
    try {
      const [adminsRes, rolesRes, orgsRes] = await Promise.all([
        axios.get(`${PLATFORM_API}/admins`, authHeaders()),
        axios.get(`${PLATFORM_API}/roles`, authHeaders()),
        axios.get(`${PLATFORM_API}/organizations`, authHeaders()),
      ]);
      setAdmins(adminsRes.data.admins || []);
      setRoles(rolesRes.data.roles || []);
      setOrgs(orgsRes.data.organizations || []);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openModal = (member = null) => {
    setEditingMember(member);
    setModalTab('basic');
    setOrgSearch('');
    if (member) {
      setForm({
        username: member.username,
        name: member.name || '',
        email: member.email || '',
        password: '',
        role_id: member.role_id || '',
        commission_rate: member.commission_rate ?? 0,
        is_active: member.is_active ?? true,
        phone: member.phone || '',
        two_factor_channels: (member.two_factor_channels && member.two_factor_channels.length ? member.two_factor_channels : ['email']),
        regions: member.regions || [],
      });
      setSelectedOrgIds(new Set(orgs.filter(o => o.onboarded_by_admin_id === member.id).map(o => o.id)));
    } else {
      const partnerRole = roles.find(r => r.name.toLowerCase() === 'partner');
      setForm({ ...emptyForm, role_id: partnerRole?.id || '' });
      setSelectedOrgIds(new Set());
    }
    setModalOpen(true);
  };

  const toggleOrg = (orgId) => {
    setSelectedOrgIds(prev => {
      const next = new Set(prev);
      next.has(orgId) ? next.delete(orgId) : next.add(orgId);
      return next;
    });
  };

  const syncCompanies = async (memberId) => {
    const originally = new Set(orgs.filter(o => o.onboarded_by_admin_id === memberId).map(o => o.id));
    const toAssign = [...selectedOrgIds].filter(id => !originally.has(id));
    const toUnassign = [...originally].filter(id => !selectedOrgIds.has(id));
    await Promise.all([
      ...toAssign.map(id => axios.patch(`${PLATFORM_API}/organizations/${id}`, { onboardedByAdminId: memberId }, authHeaders())),
      ...toUnassign.map(id => axios.patch(`${PLATFORM_API}/organizations/${id}`, { onboardedByAdminId: null }, authHeaders())),
    ]);
  };

  const handleSave = async () => {
    if (!editingMember && (!form.username.trim() || !form.email.trim() || !form.password)) {
      toast.error('Username, email and password are required');
      setModalTab('basic');
      return;
    }
    if (!form.two_factor_channels.length) {
      toast.error('Pick at least one 2FA channel');
      setModalTab('access');
      return;
    }
    if ((form.two_factor_channels.includes('sms') || form.two_factor_channels.includes('whatsapp')) && !form.phone.trim()) {
      toast.error('Add a phone number (with country code) before enabling SMS or WhatsApp codes');
      setModalTab('access');
      return;
    }
    const doSave = async (pin, otp) => {
      setIsSaving(true);
      try {
        let memberId = editingMember?.id;
        if (editingMember) {
          const payload = {
            name: form.name,
            email: form.email,
            role_id: form.role_id || null,
            commission_rate: Number(form.commission_rate) || 0,
            is_active: form.is_active,
            regions: form.regions,
          };
          if (form.password) payload.password = form.password;
          if (me.is_owner_role) {
            payload.phone = form.phone.trim() || null;
            payload.two_factor_channels = form.two_factor_channels;
          }
          await axios.patch(`${PLATFORM_API}/admins/${memberId}`, payload, stepUpHeaders(pin, otp));
        } else {
          const createPayload = {
            username: form.username.trim(),
            name: form.name || form.username.trim(),
            email: form.email.trim(),
            password: form.password,
            role_id: form.role_id || null,
            commission_rate: Number(form.commission_rate) || 0,
          };
          if (me.is_owner_role) {
            createPayload.phone = form.phone.trim() || null;
            createPayload.two_factor_channels = form.two_factor_channels;
          }
          const res = await axios.post(`${PLATFORM_API}/auth/create`, createPayload, stepUpHeaders(pin, otp));
          memberId = res.data.admin?.id;
        }
        if (memberId) await syncCompanies(memberId);
        toast.success(editingMember ? 'Team member updated' : 'Team member added');
        setModalOpen(false);
        fetchData();
      } finally {
        setIsSaving(false);
      }
    };
    requirePin(
      editingMember ? `Save changes to team member "${form.name || form.username}"` : `Add team member "${form.username.trim()}"`,
      doSave
    );
  };

  const handleDelete = async () => {
    const member = memberToDelete;
    requirePin(`Delete team member "${member?.name || member?.username}" — this cannot be undone`, async (pin, otp) => {
      setIsDeleting(true);
      try {
        await axios.delete(`${PLATFORM_API}/admins/${member.id}`, stepUpHeaders(pin, otp));
        toast.success('Team member deleted');
        setMemberToDelete(null);
        fetchData();
      } finally {
        setIsDeleting(false);
      }
    });
  };

  const canDelete = (member) => !member.is_protected && member.username !== me.username;

  const filteredOrgs = orgs.filter(o =>
    !orgSearch || o.name?.toLowerCase().includes(orgSearch.toLowerCase()) || o.slug?.toLowerCase().includes(orgSearch.toLowerCase())
  );

  const memberCard = (member) => (
    <ResourceCard
      key={member.id}
      id={member.id}
      name={member.name || member.username}
      icon={UserIcon}
      iconColor={member.role_color || '#3B82F6'}
      topRightBadge={{ label: member.role_name || 'admin', color: member.role_color || '#3B82F6' }}
      descriptionLines={[
        { icon: <Mail className="w-3 h-3" />, text: member.email || '—' },
        {
          icon: <Building2 className="w-3 h-3" />,
          text: `${member.orgs_onboarded || 0} ${member.orgs_onboarded === 1 ? 'company' : 'companies'}${member.commission_rate > 0 ? ` • ${member.commission_rate}% commission` : ''}`,
        },
        {
          icon: <Lock className="w-3 h-3" />,
          text: `2FA: ${(member.two_factor_channels || ['email']).map(c => c === 'sms' ? 'SMS' : c === 'whatsapp' ? 'WhatsApp' : 'Email').join(' + ')}`,
        },
      ]}
      additionalBadges={
        <>
          {(member.regions || []).map(r => (
            <span key={r} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700" data-testid={`member-region-badge-${member.username}-${r.toLowerCase()}`}>{r}</span>
          ))}
          {!member.is_active ? (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700">Disabled</span>
          ) : null}
        </>
      }
      onEdit={() => openModal(member)}
      onDelete={canDelete(member) ? () => setMemberToDelete(member) : undefined}
      additionalClassNames={!member.is_active ? 'opacity-60' : ''}
    />
  );

  return (
    <TabPageLayout
      title="Team"
      subtitle="BookingFlows team members with Platform Admin access"
      loading={loading}
      actions={
        <LayoutActionButton icon={Plus} onClick={() => openModal()} data-testid="team-add-member-button">
          Add
        </LayoutActionButton>
      }
    >
      <div className="space-y-6" data-testid="superadmin-team-tab">
        {admins.length === 0 ? (
          <EmptyState icon={Users} title="No Team Members" description="Add your first team member" actionLabel="Add Member" onAction={() => openModal()} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {admins.map(memberCard)}
          </div>
        )}

        {/* Add / Edit modal */}
        <StandardModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingMember ? `Edit ${editingMember.name || editingMember.username}` : 'Add Team Member'}
          size="md"
          footer={
            <>
              <SecondaryButton onClick={() => setModalOpen(false)} className="flex-1" disabled={isSaving}>Cancel</SecondaryButton>
              <SuccessButton onClick={handleSave} className="flex-1" disabled={isSaving} data-testid="member-save-button">
                {isSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : (<><Check className="w-4 h-4 mr-2" />{editingMember ? 'Save Changes' : 'Add Member'}</>)}
              </SuccessButton>
            </>
          }
        >
          <div className="mb-5">
            <ModalTabSelector
              tabs={[
                { value: 'basic', label: 'Basic', icon: UserIcon },
                { value: 'access', label: 'Access', icon: Shield },
                { value: 'companies', label: 'Companies', icon: Building2 },
              ]}
              value={modalTab}
              onChange={setModalTab}
            />
          </div>

          {modalTab === 'basic' && (
            <div className="space-y-4">
              {!editingMember && (
                <div>
                  <Label>Username <span className="text-red-500">*</span></Label>
                  <Input value={form.username} onChange={(e) => set('username', e.target.value)} placeholder="siraj" className="mt-1.5" data-testid="member-username-input" />
                </div>
              )}
              <div>
                <Label>Full Name</Label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Siraj Al Marri" className="mt-1.5" data-testid="member-name-input" />
              </div>
              <div>
                <Label>Email <span className="text-red-500">*</span></Label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="siraj@bookingflows.com" className="mt-1.5" data-testid="member-email-input" />
                <p className="text-xs text-gray-400 mt-1">Verification codes (2FA) go to the channels picked in Access → 2FA delivery.</p>
              </div>
              <div>
                <Label>{editingMember ? 'New Password (leave blank to keep)' : 'Password'} {!editingMember && <span className="text-red-500">*</span>}</Label>
                <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} className="mt-1.5" data-testid="member-password-input" />
              </div>
            </div>
          )}

          {modalTab === 'access' && (
            <div className="space-y-4">
              <div>
                <Label>Role</Label>
                <Select value={form.role_id} onValueChange={(v) => set('role_id', v)}>
                  <SelectTrigger className="mt-1.5" data-testid="member-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                          {r.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">Roles control which tabs and data this member can access — manage them in the Roles tab.</p>
              </div>
              <div>
                <Label>Commission %</Label>
                <Input
                  type="number" min="0" max="100" step="0.5"
                  value={form.commission_rate}
                  onChange={(e) => set('commission_rate', e.target.value)}
                  className="mt-1.5 w-32"
                  data-testid="member-commission-input"
                />
                <p className="text-xs text-gray-400 mt-1">% of BookingFlows platform fees generated by the companies they onboarded.</p>
              </div>
              <div>
                <Label>Regions</Label>
                <p className="text-xs text-gray-400 mt-0.5 mb-2">Scopes the onboarding pipeline — members only see companies in their regions. None selected = all regions.</p>
                <div className="flex flex-wrap gap-2" data-testid="member-regions">
                  {REGION_OPTIONS.map((region) => {
                    const on = form.regions.includes(region);
                    return (
                      <button
                        key={region}
                        type="button"
                        onClick={() => set('regions', on ? form.regions.filter(r => r !== region) : [...form.regions, region])}
                        className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        data-testid={`member-region-${region.toLowerCase()}`}
                      >
                        {on ? <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> : null}{region}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="pt-1 border-t border-gray-100">
                <Label>2FA delivery channels</Label>
                <p className="text-xs text-gray-400 mt-0.5 mb-2">Where login & step-up verification codes get sent — at least one is required.</p>
                <div className="flex flex-wrap gap-2" data-testid="member-2fa-channels">
                  {CHANNEL_OPTIONS.map(({ id, label }) => {
                    const on = form.two_factor_channels.includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        disabled={!me.is_owner_role}
                        onClick={() => {
                          if (!me.is_owner_role) return;
                          set('two_factor_channels', on
                            ? form.two_factor_channels.filter(c => c !== id)
                            : [...form.two_factor_channels, id]);
                        }}
                        className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'} ${!me.is_owner_role ? 'opacity-60 cursor-not-allowed' : ''}`}
                        data-testid={`member-2fa-channel-${id}`}
                      >
                        {on ? <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> : null}{label}
                      </button>
                    );
                  })}
                </div>
                {(form.two_factor_channels.includes('sms') || form.two_factor_channels.includes('whatsapp')) && (
                  <div className="mt-3">
                    <Label>Phone (international format)</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set('phone', e.target.value)}
                      placeholder="+9715xxxxxxxx"
                      disabled={!me.is_owner_role}
                      className="mt-1.5"
                      data-testid="member-phone-input"
                    />
                  </div>
                )}
                {!me.is_owner_role && (
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />Only the owner can change 2FA channels and phone numbers.
                  </p>
                )}
              </div>
              {editingMember && (
                <div className="flex items-center justify-between px-3.5 py-3 rounded-xl border border-gray-100 bg-gray-50/60">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Active</p>
                    <p className="text-xs text-gray-500">Disabled members can't log in (history is kept)</p>
                  </div>
                  <ServiceToggle
                    checked={form.is_active}
                    onChange={(v) => set('is_active', v)}
                    disabled={editingMember?.is_protected}
                    data-testid="member-active-switch"
                  />
                </div>
              )}
              {editingMember?.is_protected && (
                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                  <Lock className="w-3 h-3" />This is a protected owner account — it can never be deactivated or deleted.
                </p>
              )}
            </div>
          )}

          {modalTab === 'companies' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Companies this member onboarded — used for partner payout reports and scoped access.</p>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="pl-9" placeholder="Search companies..." value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} data-testid="assign-companies-search" />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredOrgs.map(org => {
                  const assignedToOther = org.onboarded_by_admin_id && org.onboarded_by_admin_id !== editingMember?.id;
                  return (
                    <label
                      key={org.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-gray-50 ${assignedToOther ? 'opacity-50' : ''}`}
                      data-testid={`assign-org-row-${org.slug}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrgIds.has(org.id)}
                        disabled={assignedToOther}
                        onChange={() => toggleOrg(org.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{org.name}</p>
                        <p className="text-xs text-gray-400 truncate">{org.slug}{assignedToOther ? ' • assigned to another rep' : ''}</p>
                      </div>
                      <span className="text-xs text-gray-400 uppercase">{org.currency || 'USD'}</span>
                    </label>
                  );
                })}
                {filteredOrgs.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No companies found</p>}
              </div>
              <p className="text-xs text-gray-400">{selectedOrgIds.size} selected</p>
            </div>
          )}
        </StandardModal>

        <DeleteConfirmModal
          isOpen={!!memberToDelete}
          onClose={() => setMemberToDelete(null)}
          onConfirm={handleDelete}
          title="Delete Team Member?"
          description={`Are you sure you want to delete "${memberToDelete?.name || memberToDelete?.username}"?`}
          isDeleting={isDeleting}
        >
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>Their company assignments will be unassigned and past payout reports for them will no longer appear. If this is an ex-partner, consider <span className="font-medium">deactivating</span> them instead (Edit → Access → Active off) to keep history.</p>
        </DeleteConfirmModal>

        <PinModal prompt={pinPrompt} onClose={() => setPinPrompt(null)} />
      </div>
    </TabPageLayout>
  );
};

export default SuperAdminTeamTab;
